/**
 * Pause, resume or delete the rows of one plan together (2026-10-31).
 *
 * "Pause both", "Delete all 3": there is no one request for the rows of
 * a plan, so each row is its own request, sent one after another. One
 * can fail where another went through -- a row removed meanwhile, the
 * network dropping between two -- and the screen then has to say which
 * rows changed and which did not, by name and time, since every row of
 * a plan has the same name. Every row is tried, so a refusal of one does
 * not leave the others unasked, and the caller reads the schedules again
 * after any failure, so what is drawn is what each row now is.
 */
import type { TaskSchedule } from '../../../api/v1';
import { networkFailureText } from '../../../api/v1-client';
import { FIRE_MODE_HOLD_COPY, isFireModeError } from '../../../api/hooks/useSchedules';
import { isMaintenanceHold, MAINTENANCE_HOLD_COPY } from '../../../api/hooks/useMaintenance';
import { planRowLabel } from './schedule-span';

export type PlanVerb = 'pause' | 'resume' | 'delete';

export interface BatchOutcome {
  /** The rows the server took, in the order sent. */
  done: string[];
  /** The rows it refused or never answered for, each with why. */
  failed: Array<{ scheduleId: string; error: unknown }>;
}

/** `act` on each row in turn; every row is tried. */
export async function eachInTurn(
  ids: readonly string[],
  act: (scheduleId: string) => Promise<unknown>,
): Promise<BatchOutcome> {
  const outcome: BatchOutcome = { done: [], failed: [] };
  for (const scheduleId of ids) {
    try {
      await act(scheduleId);
      outcome.done.push(scheduleId);
    } catch (error) {
      outcome.failed.push({ scheduleId, error });
    }
  }
  return outcome;
}

const PAST: Record<PlanVerb, string> = {
  pause: 'paused',
  resume: 'resumed',
  delete: 'deleted',
};

/** Why a request did not go through, as the end of a sentence. A
 *  request that never reached the server says so in plain words, not
 *  the browser's ("network or CORS"). */
function reasonOf(err: unknown): string {
  if (isFireModeError(err)) return FIRE_MODE_HOLD_COPY;
  if (networkFailureText(err) !== null) {
    return 'the console could not reach the server. Check the connection and try again.';
  }
  const msg = err instanceof Error ? err.message : String(err ?? '');
  const text = msg.trim() || 'the server did not say why';
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

/**
 * One schedule paused or resumed on its own that did not go through,
 * for the status line of the panel or list it was pressed in: "Not
 * paused: <why>". A hold (fire mode, a maintenance closure) is its own
 * sentence, and said as a hold rather than a failure.
 */
export function toggleFailureWords(
  enabled: boolean,
  err: unknown,
): { message: string; tone: 'hold' | 'error' } {
  if (isFireModeError(err)) return { message: FIRE_MODE_HOLD_COPY, tone: 'hold' };
  if (isMaintenanceHold(err)) return { message: MAINTENANCE_HOLD_COPY, tone: 'hold' };
  return { message: `Not ${enabled ? 'resumed' : 'paused'}: ${reasonOf(err)}`, tone: 'error' };
}

function listWords(items: readonly string[]): string {
  return items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * What a batch with a failure in it came to, row by row: `Paused "Lobby"
 * (every day at 14:00, until Tue 03/Nov). Not paused: "Lobby" (every day
 * at 15:00, from Wed 04/Nov): <why>`. "Nothing was paused" when no row
 * went through. Null when every row did -- the caller's own success line
 * says that. Rows are named from `schedules` as they were BEFORE the
 * batch (a deleted row is gone from the list read after it).
 */
export function batchFailureText(
  verb: PlanVerb,
  outcome: BatchOutcome,
  schedules: readonly TaskSchedule[],
  nowMs: number,
  siteTimeZone: string,
): string | null {
  if (outcome.failed.length === 0) return null;
  const past = PAST[verb];
  const label = (id: string) => {
    const s = schedules.find((x) => x.schedule_id === id);
    return s ? planRowLabel(s, nowMs, siteTimeZone) : 'one schedule';
  };
  const reasons = [...new Set(outcome.failed.map((f) => reasonOf(f.error)))];
  const why = reasons.join(' ');
  if (outcome.done.length === 0) return `Nothing was ${past}: ${why}`;
  const Past = past[0]!.toUpperCase() + past.slice(1);
  return `${Past} ${listWords(outcome.done.map(label))}. Not ${past}: ${listWords(
    outcome.failed.map((f) => label(f.scheduleId)),
  )}: ${why}`;
}
